import { useState, useCallback, useRef } from 'react';

import { useTranslations } from 'next-intl';
import {
  parseUnits,
  type Hash,
  type Address,
  UserRejectedRequestError,
  BaseError,
  type Abi
} from 'viem';
import {
  useAccount,
  useReadContract,
  useWriteContract,
  usePublicClient,
  useChainId
} from 'wagmi';

import { poolContractAbi, erc20Abi } from '@/abi';
import { removeCommas } from '@/lib/utils/removeCommas';
import { C_Regexp } from '@/ts/constants/C_Regexp';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';

// Proper user rejection detection using viem error types
const isUserRejection = (error: unknown): boolean => {
  if (error instanceof UserRejectedRequestError) return true;

  if (error instanceof BaseError) {
    const cause = error.cause;
    if (cause && typeof cause === 'object') {
      const causeStr = String(cause).toLowerCase();
      return (
        causeStr.includes('user rejected') ||
        causeStr.includes('user denied') ||
        causeStr.includes('rejected') ||
        causeStr.includes('denied') ||
        causeStr.includes('cancelled') ||
        causeStr.includes('canceled')
      );
    }
  }
  return false;
};

async function checkPaused(
  publicClient: ReturnType<typeof usePublicClient>,
  address?: Address,
  poolAbi?: Abi
): Promise<boolean | null> {
  if (!publicClient || !address || !poolAbi) return null;

  try {
    const p = await publicClient.readContract({
      address,
      abi: poolAbi,
      functionName: 'paused'
    });
    if (typeof p === 'boolean') return p;
  } catch {
    return null;
  }

  return null;
}

export type T_BuyTokensState = {
  isLoading: boolean;
  isApproving: boolean;
  isBuying: boolean;
  error?: string;
  txHash?: Hash;
};

export type T_BuyTokensResult = {
  success: boolean;
  txHash?: Hash;
  error?: string;
};

export function useBuyTokens(poolContractAddress: Address) {
  const t = useTranslations('pools');
  const poolAddress = poolContractAddress;
  const { address } = useAccount();
  const chainId = useChainId();
  const publicClient = usePublicClient();
  const { writeContractAsync } = useWriteContract();

  const [state, setState] = useState<T_BuyTokensState>({
    isLoading: false,
    isApproving: false,
    isBuying: false
  });

  // expose pause flags for the UI (null = unknown / method not present)
  const pauseStateRef = useRef<{ token: boolean | null; pool: boolean | null }>(
    { token: null, pool: null }
  );

  const isProcessingRef = useRef(false);

  // ABIs are properly typed from the centralized export
  const POOL_ABI = poolContractAbi;
  const ERC20_ABI = erc20Abi;

  const { data: usdcAddress } = useReadContract({
    address: poolAddress,
    abi: POOL_ABI,
    functionName: 'usdc'
  }) as { data: Address | undefined };

  const { data: usdcBalance, refetch: refetchBalance } = useReadContract({
    address: usdcAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: { enabled: !!address && !!usdcAddress }
  }) as { data: bigint | undefined; refetch: () => Promise<unknown> };

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: usdcAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args:
      address && poolAddress && usdcAddress
        ? [address, poolAddress]
        : undefined,
    query: { enabled: !!address && !!usdcAddress }
  }) as { data: bigint | undefined; refetch: () => Promise<unknown> };

  const buyTokens = useCallback(
    async (usdcAmountInput: string): Promise<T_BuyTokensResult> => {
      if (isProcessingRef.current) {
        return { success: false, error: t('inProgress') };
      }

      // Early validation - don't set processing flag yet
      if (!address) return { success: false, error: t('walletNotConnected') };
      if (!chainId) return { success: false, error: t('wrongNetwork') };

      // Remove commas before validation (e.g., "1,000" -> "1000")
      let usdcAmountWithoutCommas = removeCommas(usdcAmountInput);
      if (usdcAmountWithoutCommas.startsWith('.')) {
        usdcAmountWithoutCommas = '0' + usdcAmountWithoutCommas;
      }
      if (!C_Regexp.numberWith6Decimals.test(usdcAmountWithoutCommas)) {
        return { success: false, error: t('invalidInput') };
      }

      // Check if pool is paused before any writes
      const poolPaused = await checkPaused(publicClient, poolAddress, POOL_ABI);
      // cache for UI
      pauseStateRef.current = { token: null, pool: poolPaused };

      if (poolPaused === true) {
        return { success: false, error: t('poolPaused') };
      }

      // Now set processing flag since validation passed
      isProcessingRef.current = true;
      setState(prev => ({ ...prev, isLoading: true, error: undefined }));

      try {
        if (!usdcAddress) throw new Error(t('usdcAddressNotAvailable'));

        const usdcAmountBigInt = parseUnits(
          usdcAmountWithoutCommas,
          C_USDC_DECIMALS
        );
        // Get fresh balance and allowance before making spending decisions
        const [freshBalance, freshAllowance] = await Promise.all([
          (publicClient?.readContract({
            address: usdcAddress,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [address]
          }) as Promise<bigint>) || 0n,
          (publicClient?.readContract({
            address: usdcAddress,
            abi: ERC20_ABI,
            functionName: 'allowance',
            args: [address, poolAddress]
          }) as Promise<bigint>) || 0n
        ]);

        if (freshBalance < usdcAmountBigInt) {
          throw new Error(t('insufficientBalance'));
        }

        if (freshAllowance < usdcAmountBigInt) {
          setState(prev => ({ ...prev, isApproving: true }));

          const simulateApprove = await publicClient
            ?.simulateContract({
              address: usdcAddress,
              abi: ERC20_ABI,
              functionName: 'approve',
              args: [poolAddress, usdcAmountBigInt],
              account: address
            })
            .catch(() => null);
          if (!simulateApprove) throw new Error(t('allowanceFailed'));

          const approveHash = await writeContractAsync(simulateApprove.request);
          await publicClient?.waitForTransactionReceipt({ hash: approveHash });
          setState(prev => ({ ...prev, isApproving: false }));
          try {
            await refetchAllowance();
          } catch {}
        }

        const simulateBuy = await publicClient
          ?.simulateContract({
            address: poolAddress,
            abi: POOL_ABI,
            functionName: 'buyTokens',
            args: [usdcAmountBigInt],
            account: address
          })
          .catch(() => null);
        if (!simulateBuy) throw new Error(t('buyFailed'));

        setState(prev => ({ ...prev, isBuying: true }));

        const buyHash = await writeContractAsync(simulateBuy.request);
        await publicClient?.waitForTransactionReceipt({ hash: buyHash });

        try {
          await refetchBalance();
        } catch {}
        try {
          await refetchAllowance();
        } catch {}

        setState(prev => ({
          ...prev,
          isLoading: false,
          isBuying: false,
          txHash: buyHash
        }));
        return { success: true, txHash: buyHash };
      } catch (e) {
        const msg = e instanceof Error ? e.message : t('unknownError');

        const userRejected = isUserRejection(e);
        const errorMessage = userRejected ? t('transactionRejected') : msg;

        setState(prev => ({
          ...prev,
          isLoading: false,
          isApproving: false,
          isBuying: false,
          error: errorMessage
        }));
        return { success: false, error: errorMessage };
      } finally {
        isProcessingRef.current = false;
      }
    },
    [
      address,
      chainId,
      t,
      usdcAddress,
      publicClient,
      ERC20_ABI,
      poolAddress,
      POOL_ABI,
      writeContractAsync,
      refetchAllowance,
      refetchBalance
    ]
  );

  const isReady = !!address && !!usdcAddress;

  return {
    buyTokens,
    state,
    usdcBalance,
    allowance,
    isReady,
    paused: pauseStateRef.current
  };
}
